import { lazy } from 'react';
import { Redirect } from 'react-router';
import AuthLayout from '../layout/auth-layout';
import MainLayout from '../layout/main-layout';
import paths from './paths';
import { needAuth, needOrgId, needRedirectCauseHasOrgId, needUnAuth } from './router-middleware';

const ImportParticipantByExcel = lazy(() => import('../pages/import-participant'));
const ImportParticipantResult = lazy(() => import('../pages/import-participant-result'));
const ImportRemittanceByExcel = lazy(() => import('../pages/import-remittance'));
const ImportRemittanceResult = lazy(() => import('../pages/import-remittance-result'));
const ImportROCByExcel = lazy(() => import('../pages/import-roc'));
const ImportROCResult = lazy(() => import('../pages/import-roc-result'));
const ImportShiftAsyncResult = lazy(() => import('../pages/import-service-delivery-async-result'));
const Products = lazy(() => import('../pages/products'));
const NdisNotifications = lazy(() => import('../pages/ndis-notifications'));
const Contacts = lazy(() => import('../pages/contacts'));
const CategoryY = lazy(() => import('../pages/categories/category-y'));
const CategoryX = lazy(() => import('../pages/categories/category-x'));
const Registrations = lazy(() => import('../pages/registrations'));
const Remittance = lazy(() => import('../pages/remittance'));
const InvoiceBatchHistory = lazy(() => import('../pages/invoice-batch-history'));
const InvoiceBatchDetails = lazy(() => import('../pages/invoice-details'));
const InvoiceInError = lazy(() => import('../pages/invoices-in-error'));
const Profile = lazy(() => import('../pages/profile'));
const Welcome = lazy(() => import('../pages/welcome'));
const Login = lazy(() => import('../pages/login'));
const Lock = lazy(() => import('../pages/lock'));
const AccessDenied = lazy(() => import('../pages/access-denied'));
const Register = lazy(() => import('../pages/register'));
const ForgetPassword = lazy(() => import('../pages/forget-password'));
const Callback = lazy(() => import('../pages/callback'));
const TermsAndConditions = lazy(() => import('../pages/terms-and-conditions'));
const Test = lazy(() => import('../pages/test'));
const Settings = lazy(() => import('../pages/settings'));
const SelectOrg = lazy(() => import('../pages/select-organisation'));
const Participants = lazy(() => import('../pages/participants'));
const Budget = lazy(() => import('../pages/participant-budget'));
const Analytics = lazy(() => import('../pages/analytics'));
const ServiceBookings = lazy(() => import('../pages/service-bookings'));
const Participant360View = lazy(() => import('../pages/participant-360-view'));
const Integration = lazy(() => import('../pages/integration'));
const RosterOfCare = lazy(() => import('../pages/roster-of-care'));
const ServiceDelivery = lazy(() => import('../pages/service-delivery'));
const ReadyToClaim = lazy(() => import('../pages/ready-to-claim'));
const RecoveryClaim = lazy(() => import('../pages/recovery-claim'));
const SuccessfulClaim = lazy(() => import('../pages/successful-claims'));
const SuccessfulClaimsNewGrid = lazy(() => import('../pages/successful-claim-new-grid'));
const PaymentCancellation = lazy(() => import('../pages/payment-cancellation'));
const ClaimBatchHistory = lazy(() => import('../pages/claim-batch-history'));
const ClaimBatchDetail = lazy(() => import('../pages/claim-batch-detail'));
const ClaimsInError = lazy(() => import('../pages/claims-in-error'));
const LegalEntityManagement = lazy(() => import('../pages/legal-entity-management'));
const ReadyToInvoice = lazy(() => import('../pages/ready-to-invoice'));
const IssuedInvoices = lazy(() => import('../pages/Issued-invoices'));
const Transactions = lazy(() => import('../pages/transactions'));
const IssuedInvoicesV2 = lazy(() => import('../pages/issued-invoices-v2'));
const InvoiceBatchHistoryV2 = lazy(() => import('../pages/invoice-batch-history-v2'));
const InvoiceDetailsV2 = lazy(() => import('../pages/invoice-details-v2'));
const InvoiceBatches = lazy(() => import('../pages/invoice-batches'));
const Invoices = lazy(() => import('../pages/invoices'));
const PaymentsV2 = lazy(() => import('../pages/ar-payments'));
const PayerStatements = lazy(() => import('../pages/ar-payer-statements'));
const ArSettlement = lazy(() => import('../pages/ar-settlement'));
const CreateServiceBooking = lazy(() => import('../pages/create-service-booking'));
const SupportAtHomeCareRecipient360 = lazy(
  () => import('../pages/sah/sah-care-recipient-360/care-recipient-360-view'),
);
const Labels = lazy(() => import('../pages/labels'));
const SupportAtHomeCareRecipients = lazy(() => import('../pages/sah/sah-care-recipients'));
const SupportAtHomeTransactions = lazy(() => import('../pages/sah/sah-transactions'));
const SupportAtHomeReadyToClaim = lazy(() => import('../pages/sah/sah-ready-to-claim'));
const SupportAtHomeClaims = lazy(() => import('../pages/sah/sah-claims'));
const SupportAtHomeClaimHistory = lazy(() => import('../pages/sah/sah-claim-history'));
const SupportAtHomeReadyToInvoice = lazy(() => import('../pages/sah/sah-ready-to-invoice'));
const SupportAtHomeMonthlyStatements = lazy(() => import('../pages/sah/sah-monthly-statements'));
const SupportAtHomeCareRecipientStatements = lazy(
  () => import('../pages/sah/sah-care-recipient-statements'),
);
const SupportAtHomeStatementSettings = lazy(() => import('../pages/sah/sah-statement-settings'));
const SupportAtHomeProviders = lazy(() => import('../pages/sah/sah-providers'));
const SupportAtHomeTransactionImportHistory = lazy(
  () => import('../pages/sah/sah-transaction-import-history'),
);
const SupportAtHomeCareRecipientImportHistory = lazy(
  () => import('../pages/sah/sah-care-recipient-import-history'),
);
const SupportAtHomePricing = lazy(() => import('../pages/sah/sah-pricing'));
const SupportAtHomeBudget = lazy(() => import('../pages/sah/sah-budget'));
const SupportAtHomeBudgetDetails = lazy(() => import('../pages/sah/sah-budget-details'));
const PersonManagement = lazy(() => import('../pages/person-management'));
const PersonDetails = lazy(() => import('../pages/person-details'));

const routes = [
  {
    path: '/app/:path?',
    layout: MainLayout,
    middleware: [needAuth],
    children: [
      {
        path: paths.analytics,
        component: () => <Analytics key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.profile,
        component: () => <Profile key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.lock,
        component: () => <Lock key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.accessDenied,
        component: () => <AccessDenied key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },

      {
        path: paths.registrations,
        component: () => <Registrations key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.products,
        component: () => <Products key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.categoryX,
        component: () => <CategoryX key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.categoryY,
        component: () => <CategoryY key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.financialContact,
        component: () => <Contacts key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.ndisNotifications,
        component: () => <NdisNotifications key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.labels,
        component: () => <Labels key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.legalEntityManagement,
        component: () => <LegalEntityManagement key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      // Backwards-compatible redirects (old /app/* → new /app/ar/*)
      {
        path: '/app/person-management',
        component: () => <Redirect to={paths.personManagement} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/app/person-management/:personId',
        component: ({ match }) => (
          <Redirect
            to={paths.personDetails.replace(':personId', String(match?.params?.personId ?? ''))}
          />
        ),
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/app/invoices',
        component: () => <Redirect to={paths.invoices} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/app/invoices/:invoiceId',
        component: ({ match }) => (
          <Redirect
            to={paths.invoiceDetailsV2.replace(
              ':invoiceId',
              String(match?.params?.invoiceId ?? ''),
            )}
          />
        ),
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/app/invoice-batches',
        component: () => <Redirect to={paths.invoiceBatches} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/app/payments',
        component: () => <Redirect to={paths.paymentsV2} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoices,
        component: () => <Invoices key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoiceBatches,
        component: () => <InvoiceBatches key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoiceDetailsV2,
        component: () => <InvoiceDetailsV2 key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.payerStatements,
        component: () => <PayerStatements key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.personManagement,
        component: () => <PersonManagement key={performance.now()} />,
        middleware: [needAuth, needOrgId],
        exact: true,
      },
      {
        path: paths.personDetails,
        component: () => <PersonDetails key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.paymentsV2,
        component: () => <PaymentsV2 key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.arSettlement,
        component: () => <ArSettlement key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.integration,
        component: () => <Integration key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/app/404',
        component: lazy(() => import('../pages/error')),
      },
      { path: '*', component: () => <Redirect to="/app/404" /> },
    ],
  },
  {
    path: '/ndis/:path?',
    layout: MainLayout,
    middleware: [needAuth],
    children: [
      {
        path: paths.serviceBookings,
        component: () => <ServiceBookings key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.participant360View,
        component: () => <Participant360View key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.participants,
        component: () => <Participants key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.rosterOfCare,
        component: () => <RosterOfCare key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.serviceDelivery,
        component: () => <ServiceDelivery key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.readyToClaim,
        component: () => <ReadyToClaim key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.readyToInvoice,
        component: () => <ReadyToInvoice key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.recoveryClaim,
        component: () => <RecoveryClaim key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.successfulClaims,
        component: () => <SuccessfulClaim key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.successfulClaimsNewGrid,
        component: () => <SuccessfulClaimsNewGrid key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.paymentCancellation,
        component: () => <PaymentCancellation key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.claimBatchHistory,
        component: () => <ClaimBatchHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.claimBatchDetail,
        component: () => <ClaimBatchDetail key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.claimsInError,
        component: () => <ClaimsInError key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.issuedInvoices,
        component: () => <IssuedInvoices key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoiceBatchHistory,
        component: () => <InvoiceBatchHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.remittance,
        component: () => <Remittance key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoiceDetails,
        component: () => <InvoiceBatchDetails key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoicesInError,
        component: () => <InvoiceInError key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.budget,
        component: () => <Budget key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.createServiceBooking,
        component: () => <CreateServiceBooking key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importParticipantByExcel,
        component: () => <ImportParticipantByExcel key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importParticipantResult,
        component: () => <ImportParticipantResult key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importROCByExcel,
        component: () => <ImportROCByExcel key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importROCResult,
        component: () => <ImportROCResult key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importRemittanceByExcel,
        component: () => <ImportRemittanceByExcel key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importRemittanceResult,
        component: () => <ImportRemittanceResult key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importShiftAsyncResult,
        component: () => <ImportShiftAsyncResult key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.transactions,
        component: () => <Transactions key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.issuedInvoicesV2,
        component: () => <IssuedInvoicesV2 key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoiceBatchHistoryV2,
        component: () => <InvoiceBatchHistoryV2 key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },

      // {
      //   path: '/app',
      //   component: RootRedirect,
      //   middleware: [needAuth],
      //   exact: true,
      // },
      {
        path: '/ndis/404',
        component: lazy(() => import('../pages/error')),
      },
      { path: '*', component: () => <Redirect to="/ndis/404" /> },
    ],
  },
  {
    path: '/support-at-home/:path?',
    layout: MainLayout,
    middleware: [needAuth],
    children: [
      {
        path: paths.supportAtHomeCareRecipient360,
        component: () => <SupportAtHomeCareRecipient360 key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeCareRecipients,
        component: () => <SupportAtHomeCareRecipients key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeTransactions,
        component: () => <SupportAtHomeTransactions key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeReadyToClaim,
        component: () => <SupportAtHomeReadyToClaim key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeClaims,
        component: () => <SupportAtHomeClaims key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeClaimHistory,
        component: () => <SupportAtHomeClaimHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeReadyToInvoice,
        component: () => <SupportAtHomeReadyToInvoice key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeMonthlyStatements,
        component: () => <SupportAtHomeMonthlyStatements key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeCareRecipientStatements,
        component: () => <SupportAtHomeCareRecipientStatements key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeStatementSettings,
        component: () => <SupportAtHomeStatementSettings key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeProviders,
        component: () => <SupportAtHomeProviders key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeTransactionImportHistory,
        component: () => <SupportAtHomeTransactionImportHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeCareRecipientImportHistory,
        component: () => <SupportAtHomeCareRecipientImportHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomePricing,
        component: () => <SupportAtHomePricing key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeBudget,
        component: () => <SupportAtHomeBudget key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeBudgetDetails,
        component: () => <SupportAtHomeBudgetDetails key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/support-at-home/404',
        component: lazy(() => import('../pages/error')),
      },
      { path: '*', component: () => <Redirect to="/support-at-home/404" /> },
    ],
  },
  {
    path: '/',
    layout: AuthLayout,
    children: [
      {
        path: paths.login,
        component: () => <Login />,
        middleware: [needUnAuth],
      },
      {
        path: paths.callback,
        component: () => <Callback />,
        // middleware: [needUnAuth], // TODO
      },
      {
        path: paths.register,
        component: () => <Register />,
        middleware: [needUnAuth],
      },
      {
        path: paths.forgetPassword,
        component: () => <ForgetPassword />,
        middleware: [needUnAuth],
      },
      {
        path: paths.welcome,
        component: () => <Welcome />,

        middleware: [needAuth, needRedirectCauseHasOrgId],
      },
      {
        path: paths.termsAndConditions,
        component: () => <TermsAndConditions />,
        middleware: [needAuth],
      },
      {
        path: paths.selectOrg,
        component: () => <SelectOrg />,
        middleware: [needAuth],
      },
      {
        path: '/',
        component: () => <Redirect to={paths.login} />,
      },
      {
        path: '/404',
        component: lazy(() => import('../pages/error')),
      },
      { path: '*', component: () => <Redirect to="/404" /> },
    ],
  },
];

export default routes;
